import { useMonth } from "@datepicker-react/hooks";
import {
  Box,
  Stack,
  StackProps,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { useDateCalendarContext } from "./DateCalendarContext";
import { Day } from "./Day";

export interface MonthProps {
  year: number;
  month: number;
}

export const Month = ({ year, month, ...rest }: MonthProps & StackProps) => {
  const { firstDayOfWeek, isMonthVisible, isActiveMonth, activeMonthIndex } =
    useDateCalendarContext();
  const { weekdayLabels, days, monthLabel } = useMonth({
    year,
    month,
    firstDayOfWeek,
    dayLabelFormat: (date: Date) => dayjs(date).format("D"),
    weekdayLabelFormat: (date: Date) => dayjs(date).format("dd"),
    monthLabelFormat: (date: Date) => dayjs(date).format("MMMM YYYY"),
  });

  const isMobile = useMediaQuery(({ breakpoints }: Theme) =>
    breakpoints.down("desktop")
  );

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile && isActiveMonth(year, month) && activeMonthIndex > 0) {
      ref.current?.scrollIntoView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);

  return (
    <Stack
      gap={300}
      alignItems="center"
      visibility={{
        desktop: isMonthVisible(year, month) ? "visible" : "hidden",
      }}
      ref={ref}
      data-testid={`calendar-month-${month}-${year}`}
      {...rest}
    >
      <Box
        display="flex"
        justifyContent="center"
        width="100%"
        position={{
          mobile: "sticky",
          desktop: "static",
        }}
        top={0}
        zIndex={1}
        padding={{
          mobile: 100,
          desktop: 0,
        }}
        sx={{
          backgroundColor: ({ tokens }: Theme) => tokens.surface.main,
        }}
      >
        <Typography variant="titleMedium">{monthLabel}</Typography>
      </Box>
      <Box
        display={{
          mobile: "none",
          desktop: "grid",
        }}
        gridTemplateColumns="repeat(7, 1fr)"
        justifyItems="center"
        width="100%"
        padding={{
          mobile: 100,
          desktop: 0,
        }}
      >
        {weekdayLabels.map((label) => (
          <Typography
            key={label}
            variant="labelSmallEmphasis"
            color={({ tokens }) => tokens.text.secondary}
          >
            {label}
          </Typography>
        ))}
      </Box>
      <Box
        display="grid"
        gridTemplateColumns="repeat(7, 1fr)"
        gridTemplateRows="auto repeat(5, 1fr)"
        justifyItems="center"
        alignItems="center"
        gap={100}
        width="100%"
      >
        {(
          days as {
            dayLabel: string;
            date: Date;
          }[]
        ).map((day, i) => (
          <Day key={`${day.dayLabel}${i}`} day={day} />
        ))}
      </Box>
    </Stack>
  );
};
